import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase/client";
import {
  Plan,
  PlanFeature,
  AIAgent,
  PlanWithFeatures,
  PlanAgent,
} from "@/lib/services/plans";

// Define query keys for React Query
export const QueryKeys = {
  plans: ["plans"],
  planFeatures: ["planFeatures"],
  aiAgents: ["aiAgents"],
};

/**
 * Hook to fetch all plans with their features and agents.
 * This combines data from 'plans', 'plan_features', 'plan_agents', and 'ai_agents' tables.
 */
export function usePlansWithFeatures() {
  return useQuery<PlanWithFeatures[], Error>({
    queryKey: QueryKeys.plans,
    queryFn: async () => {
      // Obtener planes, características y agentes en paralelo
      const [plansRes, featuresRes, planAgentsRes, agentsRes] = await Promise.all([
        supabase.from("plans").select("*").order("price_per_month"),
        supabase.from("plan_features").select("*"),
        supabase.from("plan_agents").select("*"),
        supabase.from("ai_agents").select("*"),
      ]);

      if (plansRes.error) throw plansRes.error;
      if (featuresRes.error) throw featuresRes.error;
      if (planAgentsRes.error) throw planAgentsRes.error;
      if (agentsRes.error) throw agentsRes.error;

      const plans = plansRes.data || [];
      const features = featuresRes.data || [];
      const planAgents = planAgentsRes.data || [];
      const agents = agentsRes.data || [];

      // Combine plans with their features and agents
      const plansWithFeatures = plans.map((plan) => ({
        ...plan,
        features: features.filter((f) => f.plan_id === plan.id),
        agents: agents.filter((agent) =>
          planAgents
            .filter((pa) => pa.plan_id === plan.id)
            .map((pa) => pa.agent_id)
            .includes(agent.id)
        ),
      }));

      return plansWithFeatures;
    },
    staleTime: 1000 * 60 * 5, // Data is considered stale after 5 minutes
    refetchOnWindowFocus: false, // Do not refetch on window focus
  });
}

/**
 * Hook to fetch a single plan by ID with its features and agents.
 */
export function usePlanById(id: string) {
  return useQuery<PlanWithFeatures | null, Error>({
    queryKey: [...QueryKeys.plans, id],
    queryFn: async () => {
      const { data: plan, error: planError } = await supabase
        .from("plans")
        .select("*")
        .eq("id", id)
        .single();

      if (planError) {
        if (planError.code === "PGRST116") return null; // No rows found
        throw planError;
      }

      if (!plan) return null;

      const { data: features, error: featuresError } = await supabase
        .from("plan_features")
        .select("*")
        .eq("plan_id", plan.id);

      if (featuresError) throw featuresError;

      const { data: planAgents, error: planAgentsError } = await supabase
        .from("plan_agents")
        .select("agent_id")
        .eq("plan_id", plan.id);

      if (planAgentsError) throw planAgentsError;

      let agentDetails: AIAgent[] = [];
      if (planAgents && planAgents.length > 0) {
        const agentIds = planAgents.map((a) => a.agent_id);
        const { data: agents, error: agentDetailsError } = await supabase
          .from("ai_agents")
          .select("*")
          .in("id", agentIds);

        if (agentDetailsError) throw agentDetailsError;
        agentDetails = agents || [];
      }

      return {
        ...plan,
        features: features || [],
        agents: agentDetails,
      };
    },
    enabled: !!id, // Only run the query if id is provided
    staleTime: 1000 * 60 * 5, // Data is considered stale after 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch the active plan for a given user.
 */
export function useActivePlanForUser(userId: string) {
  return useQuery<PlanWithFeatures | null, Error>({
    queryKey: ["activePlan", userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data: subscription, error: subscriptionError } = await supabase
        .from("subscriptions")
        .select("plan_id")
        .eq("user_id", userId)
        .eq("status", "active")
        .maybeSingle();

      if (subscriptionError) {
        throw subscriptionError;
      }

      if (!subscription) return null;

      // Use the usePlanById hook internally to fetch plan details
      const { data: planData, error: planError } = await supabase
        .from("plans")
        .select("*, plan_features(*), plan_agents(agent_id)")
        .eq("id", subscription.plan_id)
        .single();

      if (planError) throw planError;

      let agentDetails: AIAgent[] = [];
      if (planData.plan_agents && planData.plan_agents.length > 0) {
        const agentIds = planData.plan_agents.map((a: PlanAgent) => a.agent_id);
        const { data: agents, error: agentDetailsError } = await supabase
          .from("ai_agents")
          .select("*")
          .in("id", agentIds);

        if (agentDetailsError) throw agentDetailsError;
        agentDetails = agents || [];
      }

      return {
        ...planData,
        features: planData.plan_features || [],
        agents: agentDetails,
      };
    },
    enabled: !!userId, // Only run the query if userId is provided
    staleTime: 1000 * 60 * 5, // Data is considered stale after 5 minutes
    refetchOnWindowFocus: false,
  });
}
